import type { JsonSchemaType, Object as PortalObject, User, UserGroup } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Activity,
  Box,
  Database,
  Edit3,
  Eye,
  FileText,
  Link2,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  useCreateObject,
  useCustomer,
  useObject,
  useObjectCategory,
  useUpdateObject,
  useUserGroups,
} from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { EntityPermissionsTab, ViewAsDialog } from '@/features/shared/permissions';
import { SearchCombobox } from '@/shared/components/inputs';
import { DetailPageLayout, TabNav } from '@/shared/components/layout';
import { MetadataAutoForm } from '@/shared/components/metadata/metadata-auto-form';
import { MetadataBuilderWithPreview } from '@/shared/components/metadata/metadata-builder/metadata-builder-with-preview';
import { MetadataReadView } from '@/shared/components/metadata/metadata-read-view';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/shared/components/ui';
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard';
import { useViewAsState } from '@/shared/hooks/use-view-as';
import type { StatusOption, StatusValue } from '@/shared/schema';
import { scrollToTarget } from '@/shared/utils';
import {
  createEmptyMetadataSchema,
  createMetadataZodSchema,
  getFieldPermission,
  parseMetadata,
  sanitizeMetadataValues,
} from '@/shared/utils/metadata';
import { getObjectCategoryIcon } from '@/shared/utils/object-category-icons';
import {
  canUpdateService,
  HELI_PROVIDER_GROUP_KEY,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';
import { ObjectActivity } from '../components/object-activity';
import { ObjectFiles } from '../components/object-files';
import { ObjectMetadataSidemenu } from '../components/object-metadata-sidemenu';
import { useScrollSpy } from '../components/use-scroll-spy';
import { createObjectSchema } from '../schema';
import type { ObjectFormValues } from '../types';

export function ManageObject() {
  const { t, locale } = useIntl();
  const { user: currentUser } = useAuth();
  const createObject = useCreateObject();
  const updateObject = useUpdateObject();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copy');
  const categoryFromQuery = searchParams.get('category');
  const isEditMode = !!id;
  const isCopyMode = !!copyFromId;
  const [viewMode, setViewMode] = useState<'read' | 'edit'>(() => (isEditMode ? 'read' : 'edit'));
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  const [isViewAsDialogOpen, setIsViewAsDialogOpen] = useState(false);

  // Use regular or customer view object hooks based on mode
  const { data: object, isLoading: isLoadingObject } = useObject(id || '');
  const { data: objectToCopy } = useObject(copyFromId || '', {
    disabled: !copyFromId,
  });
  const { data: userGroupsData } = useUserGroups({ limit: 0, sort: 'key' });
  const userGroups = userGroupsData?.docs ?? [];
  const userGroupsById = useMemo(
    () => new Map(userGroups.map((group) => [group.id, group as UserGroup])),
    [userGroups]
  );
  const currentObject = object;

  const resolveGroupFromUser = useCallback(
    (user: User | null | undefined): UserGroup | null => {
      if (!user?.userGroup) return null;
      if (typeof user.userGroup === 'string') {
        return userGroupsById.get(user.userGroup) ?? null;
      }
      return user.userGroup as UserGroup;
    },
    [userGroupsById]
  );

  const currentGroup = resolveGroupFromUser(currentUser);
  const isCurrentGmnDomain = resolveUserGroupDomain(currentUser) === 'gmn';
  const isCurrentGmnAdmin = currentGroup?.key === 'gmn-admin';
  const canUseViewAs = isCurrentGmnAdmin && isEditMode;

  const { viewAsUser, viewAsAccess, isViewAsActive, setViewAsUserId, clearViewAs } = useViewAsState(
    {
      entityType: 'objects',
      entityId: isEditMode ? id : null,
      enabled: canUseViewAs,
    }
  );

  const effectiveUser = viewAsUser ?? currentUser;
  const effectiveGroup = resolveGroupFromUser(effectiveUser);
  const effectiveGroupKey = effectiveGroup?.key ?? null;
  const effectiveDomain = effectiveGroup?.domain ?? null;
  const isEffectiveGmnAdmin = effectiveGroupKey === 'gmn-admin';
  const isEffectiveCustomerAdmin = effectiveGroupKey === 'customer-admin';
  const isGmnUser = effectiveDomain === 'gmn';
  const isCustomer = effectiveDomain === 'customer';
  const canUpdateObjects = effectiveGroup ? canUpdateService(effectiveGroup, 'objects') : false;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    setScrollRoot(document.querySelector('main.overflow-y-auto') as HTMLElement | null);
  }, []);

  // Determine if customer can update this object
  let objectCustomerId: string | undefined;
  if (currentObject) {
    const assignment =
      effectiveGroupKey === HELI_PROVIDER_GROUP_KEY
        ? currentObject.assignedHelicopterServiceProvider
        : currentObject.assignedCustomer;
    if (assignment) {
      objectCustomerId = typeof assignment === 'object' ? assignment.id : assignment;
    }
  }

  const effectiveUserCustomerId =
    typeof effectiveUser?.customer === 'object'
      ? effectiveUser?.customer?.id
      : (effectiveUser?.customer as string | undefined);

  const canUpdateObject =
    !isLoadingObject &&
    canUpdateObjects &&
    (isEffectiveGmnAdmin ||
      (isCustomer && objectCustomerId && objectCustomerId === effectiveUserCustomerId));

  const viewAsUpdateAllowed = Boolean(viewAsAccess?.update.allowed);
  const canPreviewEditObject = isViewAsActive ? viewAsUpdateAllowed : canUpdateObject;
  const canSaveObject = !isViewAsActive && canPreviewEditObject;
  const formDisabled = isLoadingObject || !canPreviewEditObject;

  const assignedCustomerIdForRead =
    typeof currentObject?.assignedCustomer === 'object'
      ? currentObject?.assignedCustomer?.id
      : (currentObject?.assignedCustomer as string | undefined);
  const { data: assignedCustomerForRead } = useCustomer(assignedCustomerIdForRead ?? '');

  const assignedHeliProviderIdForRead =
    typeof currentObject?.assignedHelicopterServiceProvider === 'object'
      ? currentObject?.assignedHelicopterServiceProvider?.id
      : (currentObject?.assignedHelicopterServiceProvider as string | undefined);
  const { data: assignedHeliProviderForRead } = useCustomer(assignedHeliProviderIdForRead ?? '');

  // Determine the appropriate back href based on context
  const backHrefBase = isCurrentGmnDomain ? '/gmn/objects' : '/c/objects';
  const backHref = `${backHrefBase}${categoryFromQuery ? `?category=${categoryFromQuery}` : ''}`;

  const [isMetadataDrawerOpen, setIsMetadataDrawerOpen] = useState(false);
  const [categoryMetadataZodSchema, setCategoryMetadataZodSchema] = useState<
    z.ZodObject<z.ZodRawShape>
  >(z.object({}));
  const [dynamicMetadataZodSchema, setDynamicMetadataZodSchema] = useState<
    z.ZodObject<z.ZodRawShape>
  >(z.object({}));
  const [drawerSchema, setDrawerSchema] = useState<JsonSchemaType | undefined>(undefined);

  const initialValuesRef = useRef<ObjectFormValues | null>(null);

  const initialCategory = !isEditMode && !isCopyMode && categoryFromQuery ? categoryFromQuery : '';
  const form = useForm<ObjectFormValues, any, ObjectFormValues>({
    resolver: zodResolver(
      createObjectSchema(t, categoryMetadataZodSchema, dynamicMetadataZodSchema)
    ),
    defaultValues: {
      name: '',
      description: '',
      category: initialCategory,
      assignedCustomer: '',
      assignedHelicopterServiceProvider: '',
      dynamicMetadataSchema: createEmptyMetadataSchema(),
      categoryMetadataValues: {},
      dynamicMetadataValues: {},
    },
  });

  useEffect(() => {
    setViewMode(isEditMode ? 'read' : 'edit');
  }, [isEditMode, id]);

  // Track selected category ID for fetching its schema
  const selectedCategoryId = form.watch('category') || '';
  const { data: selectedCategory } = useObjectCategory(selectedCategoryId, {
    disabled: !selectedCategoryId,
  });
  const categoryMetadataSchema = selectedCategory?.metadataSchema as JsonSchemaType | undefined;

  const resetFormFromObject = useCallback(
    (obj: PortalObject, opts?: { nameOverride?: string }) => {
      if (!obj) return;
      const assignedCustomerId =
        typeof obj.assignedCustomer === 'object' ? obj.assignedCustomer?.id : obj.assignedCustomer;
      const assignedHeliProviderId =
        typeof obj.assignedHelicopterServiceProvider === 'object'
          ? obj.assignedHelicopterServiceProvider?.id
          : obj.assignedHelicopterServiceProvider;

      const categoryId = typeof obj.category === 'object' ? obj.category?.id : obj.category;

      const nextValues: ObjectFormValues = {
        name: opts?.nameOverride ?? obj.name,
        description: obj.description || '',
        category: categoryId || '',
        assignedCustomer: assignedCustomerId || '',
        assignedHelicopterServiceProvider: assignedHeliProviderId || '',
        dynamicMetadataSchema:
          (obj.dynamicMetadataSchema as JsonSchemaType) || createEmptyMetadataSchema(),
        categoryMetadataValues: parseMetadata(
          obj.categoryMetadataValues as Record<string, unknown> | undefined
        ),
        dynamicMetadataValues: parseMetadata(
          obj.dynamicMetadataValues as Record<string, unknown> | undefined
        ),
      };
      form.reset(nextValues);
      initialValuesRef.current = nextValues;
    },
    [form]
  );

  useEffect(() => {
    if (isEditMode && object) {
      resetFormFromObject(object);
    } else if (isCopyMode && objectToCopy) {
      resetFormFromObject(objectToCopy, {
        nameOverride: `${objectToCopy.name} (${t('common.copy')})`,
      });
    }
  }, [isEditMode, isCopyMode, object, objectToCopy, resetFormFromObject, t]);

  useEffect(() => {
    if (isEditMode || isCopyMode) return;
    if (!initialValuesRef.current) {
      initialValuesRef.current = form.getValues();
    }
  }, [form, isCopyMode, isEditMode]);

  // Update category metadata zod schema when category changes
  useEffect(() => {
    if (categoryMetadataSchema) {
      setCategoryMetadataZodSchema(
        createMetadataZodSchema(categoryMetadataSchema, effectiveGroupKey)
      );
    } else {
      setCategoryMetadataZodSchema(z.object({}));
    }
  }, [categoryMetadataSchema, effectiveGroupKey]);

  // Update dynamic metadata zod schema when object schema changes
  useEffect(() => {
    const objectSchema = form.watch('dynamicMetadataSchema') as JsonSchemaType | undefined;
    if (objectSchema && Object.keys(objectSchema.properties || {}).length > 0) {
      setDynamicMetadataZodSchema(createMetadataZodSchema(objectSchema, effectiveGroupKey));
    } else {
      setDynamicMetadataZodSchema(z.object({}));
    }
  }, [form.watch('dynamicMetadataSchema'), effectiveGroupKey]);

  // Sanitize category metadata values when category schema changes
  useEffect(() => {
    if (categoryMetadataSchema) {
      const currentValues = (form.getValues('categoryMetadataValues') || {}) as Record<
        string,
        unknown
      >;
      const sanitizedValues = sanitizeMetadataValues(currentValues, categoryMetadataSchema);
      if (JSON.stringify(currentValues) !== JSON.stringify(sanitizedValues)) {
        form.setValue('categoryMetadataValues', sanitizedValues, {
          shouldValidate: false,
        });
      }
    }
  }, [categoryMetadataSchema, form]);

  // Sanitize dynamic metadata values when object schema changes
  useEffect(() => {
    const objectSchema = form.getValues('dynamicMetadataSchema') as JsonSchemaType | undefined;
    if (objectSchema) {
      const currentValues = (form.getValues('dynamicMetadataValues') || {}) as Record<
        string,
        unknown
      >;
      const sanitizedValues = sanitizeMetadataValues(currentValues, objectSchema);
      if (JSON.stringify(currentValues) !== JSON.stringify(sanitizedValues)) {
        form.setValue('dynamicMetadataValues', sanitizedValues, {
          shouldValidate: false,
        });
      }
    }
  }, [form.watch('dynamicMetadataSchema'), form]);

  // Initialize drawer schema when opening
  const openMetadataDrawer = () => {
    setDrawerSchema(form.getValues().dynamicMetadataSchema);
    setIsMetadataDrawerOpen(true);
  };

  const handleMetadataBuilderSave = () => {
    if (drawerSchema) {
      form.setValue('dynamicMetadataSchema', drawerSchema);
      setDynamicMetadataZodSchema(createMetadataZodSchema(drawerSchema, effectiveGroupKey));
    }
    setIsMetadataDrawerOpen(false);
  };

  const discardChanges = useCallback(() => {
    if (isEditMode && currentObject) {
      resetFormFromObject(currentObject);
      setViewMode('read');
    } else if (isCopyMode && objectToCopy) {
      resetFormFromObject(objectToCopy, {
        nameOverride: `${objectToCopy.name} (${t('common.copy')})`,
      });
    } else if (initialValuesRef.current) {
      form.reset(initialValuesRef.current);
    }
    setDrawerSchema(undefined);
    setIsMetadataDrawerOpen(false);
  }, [currentObject, form, isCopyMode, isEditMode, objectToCopy, resetFormFromObject, t]);

  const { dialog: unsavedDialog, allowNextNavigation } = useUnsavedChangesGuard({
    when: form.formState.isDirty && viewMode === 'edit' && !isViewAsActive,
    onDiscard: discardChanges,
  });

  const previousViewAsRef = useRef(false);
  const previousViewAsUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isEditMode) return;

    if (isViewAsActive) {
      const currentViewAsUserId = viewAsUser?.id ?? null;
      const userChanged = previousViewAsUserRef.current !== currentViewAsUserId;
      if (!previousViewAsRef.current || userChanged) {
        discardChanges();
      }

      setViewMode(viewAsUpdateAllowed ? 'edit' : 'read');
      previousViewAsUserRef.current = currentViewAsUserId;
    } else if (previousViewAsRef.current) {
      setViewMode('read');
      previousViewAsUserRef.current = null;
    }

    previousViewAsRef.current = isViewAsActive;
  }, [discardChanges, isEditMode, isViewAsActive, viewAsUpdateAllowed, viewAsUser?.id]);

  useEffect(() => {
    if (!isViewAsActive || viewAsUpdateAllowed) return;
    if (viewMode !== 'read' || form.formState.isDirty) {
      discardChanges();
    }
  }, [discardChanges, form.formState.isDirty, isViewAsActive, viewAsUpdateAllowed, viewMode]);

  const handleSubmit = async (data: ObjectFormValues) => {
    if (!canSaveObject) return;

    try {
      if (isEditMode) {
        await updateObject.mutateAsync({ id, data });
        toast.success(t('objects.objectUpdatedSuccessfully'));
        setViewMode('read');
      } else {
        const createdObject = await createObject.mutateAsync(data);
        toast.success(t('objects.objectCreatedSuccessfully'));
        allowNextNavigation();
        const createdCategory =
          typeof data.category === 'string' && data.category ? data.category : null;
        const categoryQuery = createdCategory ? `?category=${createdCategory}` : '';
        navigate(`/gmn/objects/${createdObject.doc.id}/details${categoryQuery}`);
      }
    } catch (_error) {
      toast.error(t('objects.errorSavingObject'));
    }
  };

  const getPageTitle = () => {
    if (!isGmnUser) {
      return isEditMode ? t('objects.objectDetails') : t('objects.newObject');
    }
    return isEditMode
      ? t('objects.editObject')
      : isCopyMode
        ? t('objects.copyObject')
        : t('objects.newObject');
  };

  const getObjectLink = () => {
    const objectId = id ?? currentObject?.id;
    if (!objectId) return null;
    const routerBaseRaw = import.meta.env.VITE_APP_ROUTER_BASE || '';
    const routerBase = routerBaseRaw
      ? routerBaseRaw.startsWith('/')
        ? routerBaseRaw
        : `/${routerBaseRaw}`
      : '';
    const normalizedBase = routerBase.endsWith('/') ? routerBase.slice(0, -1) : routerBase;
    const path = `${normalizedBase}/objects/${objectId}/details`;
    if (typeof window === 'undefined') return path;
    return `${window.location.origin}${path}`;
  };

  const handleCopyLink = async () => {
    const link = getObjectLink();
    if (!link) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        toast.success(t('objects.copyLinkSuccess'));
        return;
      }
      throw new Error('Clipboard unavailable');
    } catch (_error) {
      toast.error(t('objects.copyLinkError'));
    }
  };

  // Tab navigation logic
  const rawTab = isEditMode ? location.pathname.split('/').pop() || 'details' : 'details';
  const currentTab = rawTab === 'files' ? 'details' : rawTab;
  const basePath = isCurrentGmnDomain ? '/gmn/objects' : '/c/objects';

  const showPermissionsTab =
    isEditMode && !isViewAsActive && (isEffectiveGmnAdmin || isEffectiveCustomerAdmin);
  const tabs = [
    { value: 'details', label: t('objects.masterData'), icon: FileText },
    ...(showPermissionsTab
      ? [{ value: 'permissions', label: t('permissions.tab'), icon: ShieldCheck }]
      : []),
    { value: 'activity', label: t('common.activity'), icon: Activity },
  ];

  const renderObjectDetailsCard = () => {
    const content =
      viewMode === 'edit' ? (
        <div className="grid grid-cols-6 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <FormLabel required>{t('common.name')}</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={formDisabled} />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          {/* Category selector - only for GMN users */}
          {isGmnUser && (
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('objectCategories.category')}</FormLabel>
                    <FormControl>
                      <SearchCombobox
                        value={field.value || ''}
                        onSelect={field.onChange}
                        collectionKey="object-categories"
                        searchKey="name"
                        clearValue=""
                        disabled={formDisabled}
                        texts={{
                          selectItemMsg: t('objectCategories.selectCategory'),
                          searchPlaceholder: t('objectCategories.selectCategory'),
                          noResultsMsg: t('objectCategories.noCategory'),
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          )}

          {/* Only show assigned customer field for GMN users not in customer view mode */}
          {isGmnUser && (
            <FormField
              control={form.control}
              name="assignedCustomer"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('customers.assignedCustomer')}</FormLabel>
                    <FormControl>
                      <SearchCombobox
                        value={field.value}
                        onSelect={field.onChange}
                        collectionKey="customers"
                        searchKey="name"
                        clearValue=""
                        disabled={formDisabled}
                        customWhere={{
                          or: [
                            { customerType: { equals: 'customer' } },
                            { customerType: { exists: false } },
                          ],
                        }}
                        texts={{
                          selectItemMsg: t('customers.selectCustomer'),
                          searchPlaceholder: t('customers.searchCustomers'),
                          noResultsMsg: t('customers.noCustomersFound'),
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          )}

          {isGmnUser && (
            <FormField
              control={form.control}
              name="assignedHelicopterServiceProvider"
              render={({ field }) => (
                <FormItem className="col-span-6 md:col-span-3 lg:col-span-2">
                  <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <FormLabel>{t('customers.assignedHelicopterServiceProvider')}</FormLabel>
                    <FormControl>
                      <SearchCombobox
                        value={field.value}
                        onSelect={field.onChange}
                        collectionKey="customers"
                        searchKey="name"
                        clearValue=""
                        disabled={formDisabled}
                        customWhere={{ customerType: { equals: 'heli-provider' } }}
                        texts={{
                          selectItemMsg: t('customers.selectHelicopterServiceProvider'),
                          searchPlaceholder: t('customers.searchHelicopterServiceProviders'),
                          noResultsMsg: t('customers.noHelicopterServiceProvidersFound'),
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="col-span-6">
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <FormLabel>{t('common.description')}</FormLabel>
                  <FormControl>
                    <Textarea {...field} disabled={formDisabled} />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        </div>
      ) : (
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-6 md:col-span-3 lg:col-span-2">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {t('common.name')}
              </div>
              <div className="text-sm font-medium">{currentObject?.name || '-'}</div>
            </div>
          </div>
          <div className="col-span-6 md:col-span-3 lg:col-span-2">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {t('objectCategories.category')}
              </div>
              <div className="text-sm font-medium">{selectedCategory?.name || '-'}</div>
            </div>
          </div>
          {isGmnUser && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('customers.assignedCustomer')}
                </div>
                <div className="text-sm font-medium">
                  {typeof currentObject?.assignedCustomer === 'object'
                    ? currentObject?.assignedCustomer?.name
                    : assignedCustomerForRead?.name || '-'}
                </div>
              </div>
            </div>
          )}
          {isGmnUser && (
            <div className="col-span-6 md:col-span-3 lg:col-span-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-1">
                  {t('customers.assignedHelicopterServiceProvider')}
                </div>
                <div className="text-sm font-medium">
                  {typeof currentObject?.assignedHelicopterServiceProvider === 'object'
                    ? currentObject?.assignedHelicopterServiceProvider?.name
                    : assignedHeliProviderForRead?.name || '-'}
                </div>
              </div>
            </div>
          )}
          <div className="col-span-6">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {t('common.description')}
              </div>
              <div className="text-sm text-foreground whitespace-pre-wrap wrap-break-words">
                {currentObject?.description || '-'}
              </div>
            </div>
          </div>
        </div>
      );

    // Keep master data consistent with metadata sections: section header + field cards only.
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4">
          <h4 className="text-sm font-semibold text-foreground">{t('objects.masterData')}</h4>
          <div className="h-px flex-1 bg-border" />
        </div>
        {content}
      </div>
    );
  };

  const hasCategoryMetadata =
    categoryMetadataSchema && Object.keys(categoryMetadataSchema.properties || {}).length > 0;

  const dynamicMetadataSchema = form.watch('dynamicMetadataSchema') as JsonSchemaType | undefined;
  const hasCustomMetadata =
    dynamicMetadataSchema && Object.keys(dynamicMetadataSchema.properties || {}).length > 0;

  const getOrderedFieldNames = (metadataSchema: JsonSchemaType) => {
    const fromOrder = metadataSchema.propertyOrder?.length ? metadataSchema.propertyOrder : [];
    return fromOrder.length > 0 ? fromOrder : Object.keys(metadataSchema.properties || {});
  };

  const getKnownGroupIds = (metadataSchema: JsonSchemaType) =>
    new Set((metadataSchema.groups ?? []).map((g) => g.id));

  const getFieldsForGroup = (metadataSchema: JsonSchemaType, groupId: string) => {
    const ordered = getOrderedFieldNames(metadataSchema);
    return ordered.filter((name) => {
      const fieldDef = metadataSchema.properties?.[name];
      if (fieldDef?.groupId !== groupId) return false;
      return getFieldPermission(fieldDef, effectiveGroupKey).read;
    });
  };

  const getUngroupedFields = (metadataSchema: JsonSchemaType) => {
    const ordered = getOrderedFieldNames(metadataSchema);
    const knownGroupIds = getKnownGroupIds(metadataSchema);
    return ordered.filter((name) => {
      const field = metadataSchema.properties?.[name];
      if (!field) return false;
      const fieldGroupId = field.groupId;
      if (!fieldGroupId) {
        return getFieldPermission(field, effectiveGroupKey).read;
      }
      if (knownGroupIds.has(fieldGroupId)) return false;
      return getFieldPermission(field, effectiveGroupKey).read;
    });
  };

  const pickZodSchema = (schema: z.ZodObject<any>, fieldNames: string[]) => {
    const shape = (schema as unknown as { shape: Record<string, unknown> }).shape || {};
    const pick: Record<string, true> = {};
    for (const name of fieldNames) {
      if (Object.hasOwn(shape, name)) pick[name] = true;
    }
    return schema.pick(pick);
  };

  const categoryGroupSections = useMemo(() => {
    if (!selectedCategoryId || !hasCategoryMetadata || !categoryMetadataSchema) return [];
    const groups = [...(categoryMetadataSchema.groups ?? [])].sort((a, b) => a.order - b.order);
    const sections = groups
      .map((group) => ({
        id: `object-category-group-${group.id}`,
        label: locale === 'en-US' && group.labelEn ? group.labelEn : group.labelDe,
        fieldNames: getFieldsForGroup(categoryMetadataSchema, group.id),
      }))
      .filter((s) => s.fieldNames.length > 0);

    const ungrouped = getUngroupedFields(categoryMetadataSchema);
    if (ungrouped.length > 0) {
      sections.push({
        id: 'object-category-ungrouped',
        label: t('metadata.builder.ungrouped'),
        fieldNames: ungrouped,
      });
    }

    return sections;
  }, [
    categoryMetadataSchema,
    hasCategoryMetadata,
    locale,
    selectedCategoryId,
    t,
    effectiveGroupKey,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(categoryMetadataSchema?.groups ?? []),
  ]);

  const customGroupSections = useMemo(() => {
    if (!hasCustomMetadata || !dynamicMetadataSchema) return [];
    const groups = [...(dynamicMetadataSchema.groups ?? [])].sort((a, b) => a.order - b.order);
    const sections = groups
      .map((group) => ({
        id: `object-custom-group-${group.id}`,
        label: locale === 'en-US' && group.labelEn ? group.labelEn : group.labelDe,
        fieldNames: getFieldsForGroup(dynamicMetadataSchema, group.id),
      }))
      .filter((s) => s.fieldNames.length > 0);

    const ungrouped = getUngroupedFields(dynamicMetadataSchema);
    if (ungrouped.length > 0) {
      sections.push({
        id: 'object-custom-ungrouped',
        label: t('metadata.builder.ungrouped'),
        fieldNames: ungrouped,
      });
    }

    return sections;
  }, [
    dynamicMetadataSchema,
    hasCustomMetadata,
    locale,
    t,
    effectiveGroupKey,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(dynamicMetadataSchema?.groups ?? []),
  ]);

  const categoryMetadataValuesWatch = useWatch({
    control: form.control,
    name: 'categoryMetadataValues',
  }) as Record<string, unknown> | undefined;

  const dynamicMetadataValuesWatch = useWatch({
    control: form.control,
    name: 'dynamicMetadataValues',
  }) as Record<string, unknown> | undefined;

  const getGroupStatusIndicator = useCallback(
    (
      metadataSchema: JsonSchemaType | undefined,
      fieldNames: string[],
      values: Record<string, unknown> | undefined
    ): {
      statusValue: StatusValue | undefined;
      statusOptions?: StatusOption[];
      icon?: string;
    } | null => {
      if (!metadataSchema?.properties) return null;

      for (const fieldName of fieldNames) {
        const fieldDef = metadataSchema.properties[fieldName] as
          | (Record<string, unknown> & { type?: string; isGroupIndicator?: boolean })
          | undefined;
        if (!fieldDef) continue;

        if (fieldDef.type === 'status' && fieldDef.isGroupIndicator === true) {
          const statusOptions = (fieldDef.statusOptions as StatusOption[] | undefined) ?? [];
          const icon = fieldDef.icon as string | undefined;
          const statusValue = values?.[fieldName] as StatusValue | undefined;
          return { statusValue, statusOptions, icon };
        }
      }

      return null;
    },
    []
  );

  const objectFiles = useMemo(
    () => (Array.isArray(currentObject?.files) ? currentObject?.files : []),
    [currentObject?.files]
  );
  const canManageFiles = isEffectiveGmnAdmin;
  const canEditFiles = canManageFiles && viewMode === 'edit' && !isViewAsActive;
  const canShowFileControls = canManageFiles && (viewMode === 'edit' || isViewAsActive);
  const showFilesSection =
    !!id && (viewMode === 'edit' || objectFiles.length > 0 || canShowFileControls);

  const sidemenuItems = useMemo(() => {
    const items: Array<{
      id: string;
      label: string;
      kind: 'master-data' | 'files' | 'category-group' | 'custom-group';
      statusIndicator?: {
        statusValue: StatusValue | undefined;
        statusOptions?: StatusOption[];
        icon?: string;
      } | null;
    }> = [
      {
        id: 'object-master-data',
        label: t('objects.masterData'),
        kind: 'master-data',
      },
    ];

    if (showFilesSection) {
      items.push({
        id: 'object-files',
        label: t('objects.files.title'),
        kind: 'files',
      });
    }

    for (const section of categoryGroupSections) {
      const statusIndicator = getGroupStatusIndicator(
        categoryMetadataSchema,
        section.fieldNames,
        categoryMetadataValuesWatch
      );
      items.push({
        id: section.id,
        label: section.label,
        kind: 'category-group',
        statusIndicator,
      });
    }
    for (const section of customGroupSections) {
      const statusIndicator = getGroupStatusIndicator(
        dynamicMetadataSchema,
        section.fieldNames,
        dynamicMetadataValuesWatch
      );
      items.push({
        id: section.id,
        label: section.label,
        kind: 'custom-group',
        statusIndicator,
      });
    }

    return items;
  }, [
    categoryGroupSections,
    categoryMetadataSchema,
    categoryMetadataValuesWatch,
    customGroupSections,
    dynamicMetadataSchema,
    dynamicMetadataValuesWatch,
    getGroupStatusIndicator,
    showFilesSection,
    t,
  ]);

  const activeSectionId = useScrollSpy(
    sidemenuItems.map((i) => i.id),
    {
      root: scrollRoot,
      rootMargin: '-15% 0px -75% 0px',
    }
  );

  const scrollToSection = (id: string) => {
    scrollToTarget(id, { root: scrollRoot });
  };

  const renderMetadataSections = ({
    schema,
    metadataSchema,
    valuesPath,
    sections,
  }: {
    schema: z.ZodObject<any>;
    metadataSchema: JsonSchemaType;
    valuesPath: string[];
    sections: Array<{ id: string; label: string; fieldNames: string[] }>;
  }) => {
    if (sections.length === 0) {
      return <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>;
    }

    return (
      <div className="space-y-10">
        {sections.map((section) => (
          <div key={section.id}>
            <div id={section.id} className="scroll-mt-28">
              <div className="flex items-center gap-3 mb-4">
                <h4 className="text-sm font-semibold text-foreground">{section.label}</h4>
                <div className="h-px flex-1 bg-border" />
              </div>
            </div>
            <MetadataAutoForm
              schema={pickZodSchema(schema, section.fieldNames)}
              form={form}
              path={valuesPath}
              metadataSchema={metadataSchema}
              groupKey={effectiveGroupKey}
              disabled={formDisabled}
              disableGrouping={true}
            />
          </div>
        ))}
      </div>
    );
  };

  const categoryMetadataValues = useMemo(
    () =>
      parseMetadata(currentObject?.categoryMetadataValues as Record<string, unknown> | undefined),
    [currentObject?.categoryMetadataValues]
  );
  const customMetadataValues = useMemo(
    () =>
      parseMetadata(currentObject?.dynamicMetadataValues as Record<string, unknown> | undefined),
    [currentObject?.dynamicMetadataValues]
  );

  const renderMetadataReadSections = ({
    metadataSchema,
    values,
    sections,
  }: {
    metadataSchema: JsonSchemaType;
    values: Record<string, unknown> | undefined;
    sections: Array<{ id: string; label: string; fieldNames: string[] }>;
  }) => {
    if (sections.length === 0) {
      return <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>;
    }

    return (
      <div className="space-y-10">
        {sections.map((section) => (
          <div key={section.id}>
            <div id={section.id} className="scroll-mt-28">
              <div className="flex items-center gap-3 mb-4">
                <h4 className="text-sm font-semibold text-foreground">{section.label}</h4>
                <div className="h-px flex-1 bg-border" />
              </div>
            </div>
            <MetadataReadView
              metadataSchema={metadataSchema}
              values={values}
              fieldNames={section.fieldNames}
              groupKey={effectiveGroupKey}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderCategoryMetadataCard = () => {
    if (!selectedCategoryId || !hasCategoryMetadata || !categoryMetadataSchema) return null;

    return viewMode === 'edit' ? (
      <div>
        {renderMetadataSections({
          schema: categoryMetadataZodSchema,
          metadataSchema: categoryMetadataSchema,
          valuesPath: ['categoryMetadataValues'],
          sections: categoryGroupSections,
        })}
      </div>
    ) : (
      <div>
        {renderMetadataReadSections({
          metadataSchema: categoryMetadataSchema,
          values: categoryMetadataValues,
          sections: categoryGroupSections,
        })}
      </div>
    );
  };

  const renderCustomMetadataCard = () => {
    const showEmptyState = viewMode === 'edit' && isEffectiveGmnAdmin && !isViewAsActive;
    if (!showEmptyState && (!hasCustomMetadata || !dynamicMetadataSchema)) return null;

    return (
      <div>
        <div id="object-custom-metadata" className="scroll-mt-28">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Database className="size-4 text-muted-foreground" />
              <h4 className="text-sm font-semibold text-foreground">
                {t('metadata.customMetadata')}
              </h4>
            </div>
            {isEffectiveGmnAdmin && viewMode === 'edit' && !isViewAsActive && (
              <Button variant="outline" size="sm" onClick={openMetadataDrawer} type="button">
                <Edit3 className="size-3!" />
                {t('metadata.addCustomMetadata')}
              </Button>
            )}
          </div>
        </div>

        {viewMode === 'edit' ? (
          hasCustomMetadata && dynamicMetadataSchema ? (
            renderMetadataSections({
              schema: dynamicMetadataZodSchema,
              metadataSchema: dynamicMetadataSchema,
              valuesPath: ['dynamicMetadataValues'],
              sections: customGroupSections,
            })
          ) : (
            <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>
          )
        ) : hasCustomMetadata && dynamicMetadataSchema ? (
          renderMetadataReadSections({
            metadataSchema: dynamicMetadataSchema,
            values: customMetadataValues,
            sections: customGroupSections,
          })
        ) : null}
      </div>
    );
  };

  const renderContent = () => {
    switch (currentTab) {
      case 'details':
        return (
          <div className="grid gap-6">
            <Form {...form}>
              <div className="grid gap-6 lg:grid-cols-12">
                <div className="lg:col-span-3">
                  {/* Mobile: jump select */}
                  <div className="lg:hidden">
                    <Select
                      value={activeSectionId ?? sidemenuItems[0]?.id ?? 'object-master-data'}
                      onValueChange={(id) => scrollToSection(id)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sidemenuItems.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Desktop: sticky sidemenu */}
                  <div className="hidden lg:block sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-auto">
                    <ObjectMetadataSidemenu
                      items={sidemenuItems}
                      activeId={activeSectionId}
                      onSelect={scrollToSection}
                      showCustomSeparator={customGroupSections.length > 0}
                    />
                  </div>
                </div>

                <div className="lg:col-span-9 grid gap-6">
                  <div id="object-master-data" className="scroll-mt-28">
                    {renderObjectDetailsCard()}
                  </div>

                  {showFilesSection && (
                    <div id="object-files" className="scroll-mt-28">
                      <ObjectFiles
                        objectId={id}
                        object={currentObject}
                        canManage={canShowFileControls}
                        readOnly={!canEditFiles}
                        variant={viewMode === 'edit' ? 'full' : 'compact'}
                      />
                    </div>
                  )}

                  {renderCategoryMetadataCard()}

                  {/* Minimal separator between category + custom metadata in the main column */}
                  {hasCategoryMetadata && hasCustomMetadata && (
                    <div className="h-px bg-border my-1" aria-hidden="true" />
                  )}

                  {renderCustomMetadataCard()}
                </div>
              </div>
            </Form>
          </div>
        );
      case 'activity':
        return <ObjectActivity objectId={id} groupKey={effectiveGroupKey} />;
      case 'files':
        return <ObjectFiles objectId={id} object={currentObject} canManage={false} readOnly />;
      case 'permissions':
        if (!showPermissionsTab || !id) {
          return (
            <div className="text-sm text-muted-foreground">{t('permissions.notAuthorized')}</div>
          );
        }
        return (
          <EntityPermissionsTab
            entityId={id}
            entityType="objects"
            customerId={objectCustomerId ?? null}
          />
        );
      default:
        return null;
    }
  };

  const showHeaderActions = currentTab === 'details';
  const headerTitle = isEditMode && currentObject ? currentObject.name : getPageTitle();

  const assignedCustomerName =
    typeof currentObject?.assignedCustomer === 'object'
      ? currentObject?.assignedCustomer?.name
      : assignedCustomerForRead?.name;
  const assignedHeliProviderName =
    typeof currentObject?.assignedHelicopterServiceProvider === 'object'
      ? currentObject?.assignedHelicopterServiceProvider?.name
      : assignedHeliProviderForRead?.name;

  const viewAsUserLabel = useMemo(() => {
    if (!viewAsUser) return '';
    const fullName = `${viewAsUser.firstName ?? ''} ${viewAsUser.lastName ?? ''}`.trim();
    return fullName !== '' ? fullName : viewAsUser.email || viewAsUser.id;
  }, [viewAsUser]);

  const viewAsControls = canUseViewAs ? (
    <div className="flex flex-wrap items-center gap-2">
      {isViewAsActive && viewAsUserLabel ? (
        <Badge variant="outline-solid" data-testid="view-as-indicator">
          <Eye size={14} className="mr-1" />
          {t('permissions.viewAs.activeLabel', { name: viewAsUserLabel })}
        </Badge>
      ) : null}
      {!isViewAsActive ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsViewAsDialogOpen(true)}
          data-testid="view-as-open"
        >
          <Eye size={16} className="mr-2" />
          {t('permissions.viewAs.label')}
        </Button>
      ) : null}
      {isViewAsActive ? (
        <Button type="button" variant="outline" onClick={clearViewAs} data-testid="view-as-exit">
          <X size={16} className="mr-2" />
          {t('permissions.viewAs.exit')}
        </Button>
      ) : null}
    </div>
  ) : null;

  const metaItems =
    isEditMode && currentObject
      ? [
          ...(isGmnUser && currentObject.assignedCustomer
            ? [
                {
                  icon: Users,
                  label:
                    assignedCustomerName && assignedCustomerName.length > 0
                      ? assignedCustomerName
                      : '-',
                },
              ]
            : []),
          ...(isGmnUser && currentObject.assignedHelicopterServiceProvider
            ? [
                {
                  icon: Users,
                  label:
                    assignedHeliProviderName && assignedHeliProviderName.length > 0
                      ? t('customers.helicopterServiceProviderLabel', {
                          name: assignedHeliProviderName,
                        })
                      : t('customers.helicopterServiceProviderLabel', { name: '-' }),
                },
              ]
            : []),
          ...(selectedCategory?.name
            ? [
                {
                  icon: getObjectCategoryIcon(selectedCategory.icon),
                  label: selectedCategory.name,
                },
              ]
            : []),
        ]
      : undefined;

  const headerIcon = selectedCategory ? getObjectCategoryIcon(selectedCategory.icon) : Box;

  return (
    <DetailPageLayout
      containerProps={{ animation: 'slide-right' }}
      headerProps={{
        backHref,
        backLabel: t('objects.backToOverview'),
        title: headerTitle,
        icon: headerIcon,
        isCreateMode: !isEditMode,
        metaItems,
        subtitle: !isEditMode ? t('objects.createNewObjectHint') : undefined,
        actions:
          showHeaderActions && isEditMode && (viewMode === 'read' || isViewAsActive) ? (
            <div className="flex flex-wrap items-center gap-3">
              {viewAsControls}
              {viewMode === 'read' && !isViewAsActive ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleCopyLink()}
                  data-testid="object-copy-link"
                >
                  <Link2 size={16} className="mr-2" />
                  {t('objects.copyLink')}
                </Button>
              ) : null}
              {viewMode === 'read' && canSaveObject ? (
                <Button
                  type="button"
                  onClick={() => setViewMode('edit')}
                  data-testid="object-enter-edit"
                >
                  <Edit3 size={16} className="mr-2" />
                  {t('common.edit')}
                </Button>
              ) : null}
            </div>
          ) : undefined,
        secondaryAction:
          showHeaderActions && !isViewAsActive
            ? isEditMode
              ? viewMode === 'edit'
                ? {
                    label: t('common.discard'),
                    onClick: discardChanges,
                  }
                : undefined
              : {
                  label: t('common.discard'),
                  onClick: () => navigate(backHref),
                }
            : undefined,
        primaryAction:
          showHeaderActions && canSaveObject && (isEditMode ? viewMode === 'edit' : true)
            ? {
                label: (isEditMode ? updateObject.isPending : createObject.isPending)
                  ? isEditMode
                    ? t('common.updating')
                    : t('common.creating')
                  : isEditMode
                    ? t('common.update')
                    : t('common.create'),
                onClick: () => void form.handleSubmit(handleSubmit)(),
                disabled:
                  (isEditMode ? updateObject.isPending : createObject.isPending) || !canSaveObject,
                loading: isEditMode ? updateObject.isPending : createObject.isPending,
              }
            : undefined,
      }}
    >
      {isEditMode && <TabNav tabs={tabs} basePath={`${basePath}/${id}`} variant="underline" />}

      <form id="object-form" onSubmit={form.handleSubmit(handleSubmit)}>
        {renderContent()}
      </form>

      {isEffectiveGmnAdmin && !isViewAsActive && (
        <Dialog open={isMetadataDrawerOpen} onOpenChange={setIsMetadataDrawerOpen}>
          <DialogContent
            className="max-w-[90vw]! w-[1400px]! max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0"
            showCloseButton={false}
          >
            <div className="flex-1 overflow-hidden">
              <div className="h-[70vh]">
                <MetadataBuilderWithPreview
                  value={drawerSchema}
                  onChange={setDrawerSchema}
                  userGroups={userGroups}
                  header={false}
                  storageKey="gmn.metadataBuilder.objectDrawer.formPreview.open"
                />
              </div>
            </div>
            <DialogFooter className="border-t p-5">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsMetadataDrawerOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button type="button" onClick={handleMetadataBuilderSave}>
                {t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {canUseViewAs && isEditMode && id ? (
        <ViewAsDialog
          open={isViewAsDialogOpen}
          onOpenChange={setIsViewAsDialogOpen}
          entityType="objects"
          entityId={id}
          customerId={objectCustomerId ?? null}
          selectedUserId={viewAsUser?.id ?? null}
          onSelectUser={(user) => {
            setViewAsUserId(user.id);
            setIsViewAsDialogOpen(false);
          }}
        />
      ) : null}

      {unsavedDialog}
    </DetailPageLayout>
  );
}
